import React, { useState } from 'react';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder, ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';
import { capitalizeFirstLetter } from '../../../../../../../../../GlobalComponents/Helpers';
import { buildExcelWorkbook } from '../../../../../../../../../GlobalComponents/Helpers/excel';
import moment from 'moment';

const DeltaReportModal = ({ row, jobData, handleDMTAction }) => {

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);

    const resourceName = capitalizeFirstLetter(row.source_resource_name);

    const fetchStatus = () => {
        setStatus(null);
        setConfirmOverwrite(false);
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/deltaStatus', {
            pathParams: { jobId: jobData.id },
            queryParams: { sourceResourceId: row.source_resource_id },
        }).then((results) => {
                setLoading(false);
                const data = typeof results?.data === 'string' ? JSON.parse(results.data) : results?.data;
                if (data?.success) setStatus(data);
            });
    };

    const takeSnapshot = () => {
        handleDMTAction('delta-snapshot', row.source_resource_id, (results) => {
            if (results?.data?.success) {
                ToastBuilder('success', results.data.message);
                setStatus({ has_snapshot: true, snapshot_at: results.data.snapshot_at, record_count: results.data.count });
                setConfirmOverwrite(false);
            } else {
                ToastBuilder('error', results?.data?.message || 'Delta snapshot failed');
            }
        });
    };

    const generateReport = () => {
        handleDMTAction('delta-report', row.source_resource_id, (results) => {
            if (results?.data?.success) {
                ToastBuilder('info', results.data.message);
                downloadDeltaReport(results, row.source_resource_name);
            } else {
                ToastBuilder('error', results?.data?.message || 'Delta report failed');
            }
        });
    };

    const downloadDeltaReport = (results, resourceName) => {
        const data = results?.data;
        if (!data?.success) return;

        const snapshotDate = moment.unix(data.snapshot_at).format('YYYY-MM-DD HH:mm:ss');
        const changes = data.changes || [];

        const summaryRows = [
            ['Resource', capitalizeFirstLetter(resourceName)],
            ['Snapshot Taken', snapshotDate],
            ['Snapshot Records', data.total_snapshot_records],
            ['Current Records', data.total_current_records],
            [''],
            ['Total Changes', changes.length],
            ['New', changes.filter(c => c.type === 'new').length],
            ['Modified', changes.filter(c => c.type === 'modified').length],
            ['Deleted', changes.filter(c => c.type === 'deleted').length],
            ['Unscoped', changes.filter(c => c.type === 'unscoped').length],
        ];

        // Collect all unique field names, push customFields to the end
        const allFields = new Set();
        changes.forEach(c => (c.fields || []).forEach(f => allFields.add(f.key)));
        const fieldNames = [...allFields].sort((a, b) => {
            const aCustom = a.toLowerCase().startsWith('customfield');
            const bCustom = b.toLowerCase().startsWith('customfield');
            if (aCustom !== bCustom) return aCustom ? 1 : -1;
            return a.localeCompare(b);
        });

        const deltaRows = changes.map(c => {
            const fieldMap = {};
            (c.fields || []).forEach(f => { fieldMap[f.key] = f; });

            return [
                c.source_rec_id,
                c.type,
                ...fieldNames.map(key => {
                    const f = fieldMap[key];
                    if (!f) return '';
                    if (c.type === 'modified') return `${f.previous ?? ''} → ${f.current ?? ''}`;
                    return f.value ?? '';
                }),
            ];
        });

        const instructions = [
            'Delta Report Instructions:',
            '1. This report compares the current source data against a snapshot of data already migrated to the destination.',
            '2. "Modified" records show changed fields as "Previous Value → Current Value".',
            '3. "New" records appeared in the source after the snapshot was taken.',
            '4. "Deleted" records had their record mapping deleted from our system since the snapshot.',
            '5. "Unscoped" records exist in the migration table but were not seen during the latest staging run.',
            '6. Blank cells indicate the field was not affected by the change.',
            '7. Custom fields are grouped at the end of the report.',
            '8. Metadata fields (_info) are excluded from the report.',
        ];

        buildExcelWorkbook({
            fileName: `${resourceName}_delta_report_${moment.unix(data.snapshot_at).format('YYYYMMDD')}.xlsx`,
            sheets: [
                {
                    sheetName: 'Summary',
                    instructions,
                    columns: [{ header: 'Field', width: 20 }, { header: 'Value', width: 30 }],
                    rows: summaryRows,
                },
                {
                    sheetName: 'Delta',
                    columns: [
                        { header: 'Source Rec ID', width: 18, align: 'center' },
                        { header: 'Change Type', width: 20, align: 'center' },
                        ...fieldNames.map(name => ({ header: name, width: 30 })),
                    ],
                    rows: deltaRows,
                },
            ],
        });
    };

    return (
        <ModalButtonBuilder title='Delta Report' className='btn-outline-secondary'
            onSubmit={false}
            onCancelLabel='Close'
            formId={'delta-report-rm-' + row.source_resource_id}
            onClick={fetchStatus}
            body={
                <div>
                    <p>Delta Report for <strong>{resourceName}</strong></p>
                    <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '12px 16px', marginBottom: 12 }}>
                        <div style={{ marginBottom: 10 }}>
                            <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>1. Take a Snapshot</p>
                            <p style={{ fontSize: '12px', color: '#636E72', margin: 0 }}>Capture the current source data before migration</p>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>2. Generate Report</p>
                            <p style={{ fontSize: '12px', color: '#636E72', margin: 0 }}>Compare what changed on the source since the snapshot</p>
                        </div>
                        <p style={{ fontSize: '11px', color: '#888', margin: 0 }}>Changes are categorized as <b>new</b>, <b>modified</b>, or <b>deleted</b>. Snapshots are archived to S3.</p>
                    </div>

                    {loading && <p style={{ fontSize: '12px', color: '#888', textAlign: 'center' }}>Loading snapshot status...</p>}

                    {status?.has_snapshot && !loading && (
                        <div style={{ background: '#eef6f3', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: '12px' }}>
                            <span style={{ color: '#24695C' }}>✓ Snapshot exists</span>
                            <span style={{ color: '#636E72', marginLeft: 8 }}>
                                {moment.unix(status.snapshot_at).format('MMM D, YYYY h:mm A')} — {status.record_count} record(s)
                            </span>
                        </div>
                    )}

                    {status && !status.has_snapshot && !loading && (
                        <div style={{ background: '#fef9f0', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: '12px', color: '#ba895d' }}>
                            No snapshot found. Take a snapshot to get started.
                        </div>
                    )}

                    {confirmOverwrite && (
                        <div style={{ background: '#fef0f0', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: '12px', color: '#c0392b' }}>
                            ⚠ A snapshot from <b>{moment.unix(status.snapshot_at).format('MMM D, YYYY h:mm A')}</b> already exists.
                            Taking a new snapshot will overwrite the current delta tracking data.
                            <div className='d-flex gap-2 mt-2'>
                                <ButtonBuilder label='Confirm Overwrite' className='btn-outline-danger' useSpinner={false} onClick={takeSnapshot} />
                                <ButtonBuilder label='Cancel' className='btn-outline-secondary' useSpinner={false} onClick={() => setConfirmOverwrite(false)} />
                            </div>
                        </div>
                    )}

                    {!confirmOverwrite && (
                        <div className='d-flex justify-content-center gap-2'>
                            <ButtonBuilder label='Take Snapshot' className='btn-danger' useSpinner={false}
                                disabled={loading}
                                onClick={() => {
                                    if (status?.has_snapshot) setConfirmOverwrite(true);
                                    else takeSnapshot();
                                }} />
                            <ButtonBuilder label='Generate Report' className='btn-primary' useSpinner={false}
                                disabled={loading || !status?.has_snapshot}
                                onClick={generateReport} />
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default DeltaReportModal;
